import React, { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Code, Visibility } from '@mui/icons-material';
import ExercisePreview from './ExercisePreview';

function parseContentJson(text) {
  const parsed = JSON.parse(text);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('Le contenu doit être un objet JSON');
  }
  return parsed;
}

export default function EditableExerciseContentPanel({
  label,
  chipColor = 'default',
  borderColor,
  gameType,
  content,
  onContentChange,
}) {
  const [mode, setMode] = useState('preview');
  const [jsonText, setJsonText] = useState(() => JSON.stringify(content ?? {}, null, 2));
  const [parsedContent, setParsedContent] = useState(content);
  const [parseError, setParseError] = useState(null);

  const applyJsonText = (text) => {
    setJsonText(text);
    try {
      const parsed = parseContentJson(text);
      setParseError(null);
      setParsedContent(parsed);
      onContentChange?.(parsed, null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'JSON invalide';
      setParseError(message);
      onContentChange?.(null, message);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...(borderColor ? { borderColor } : {}),
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Chip label={label} color={chipColor} size="small" />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, value) => value && setMode(value)}
        >
          <ToggleButton value="preview">
            <Visibility fontSize="small" sx={{ mr: 0.5 }} />
            Aperçu
          </ToggleButton>
          <ToggleButton value="json">
            <Code fontSize="small" sx={{ mr: 0.5 }} />
            JSON
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === 'preview' ? (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <ExercisePreview gameType={gameType} content={parsedContent} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            multiline
            fullWidth
            minRows={14}
            maxRows={22}
            value={jsonText}
            onChange={(e) => applyJsonText(e.target.value)}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
            variant="outlined"
            placeholder="{ ... }"
          />
          {parseError && (
            <Alert severity="error" sx={{ py: 0 }}>
              <Typography variant="caption">{parseError}</Typography>
            </Alert>
          )}
        </Box>
      )}

      {mode === 'preview' && parseError && (
        <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
          <Typography variant="caption">
            Le JSON contient une erreur — basculez sur l&apos;onglet JSON pour corriger.
          </Typography>
        </Alert>
      )}
    </Paper>
  );
}
